import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useGamesPortal } from "@/features/games-portal/hooks/use-games-portal";
import "@/games/register-all";

const GamingSystem: React.FC = () => {
  const { t } = useTranslation();
  const {
    games,
    selectedGame,
    view,
    difficulty,
    setDifficulty,
    startGame,
    backToMenu,
  } = useGamesPortal();

  return (
    <div className="flex h-full min-h-0 flex-col">
      {view === "menu" && (
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {games.length === 0 ? (
            <div className="rounded-xl border border-border-secondary bg-bg-secondary p-4 text-sm text-text-tertiary-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
              {t("games.noGames")}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {games.map((game) => (
                <button
                  key={game.id}
                  className="flex items-center gap-3 rounded-xl border border-border-secondary bg-bg-primary p-3 text-left transition-colors hover:bg-bg-tertiary dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
                  onClick={() => startGame(game.id)}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-bg-tertiary dark:bg-gray-800">
                    {game.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium text-text-primary dark:text-white">
                      {t(game.name)}
                    </h3>
                    <p className="mt-1 text-xs text-text-tertiary-600 dark:text-gray-400">
                      {t(game.description)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "playing" && selectedGame && (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-hidden">
            <selectedGame.component
              difficulty={difficulty}
              className="h-full w-full"
              onExit={backToMenu}
            />
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-border-secondary px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] dark:border-gray-700">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDifficulty("easy")}
              className={cn(
                "min-h-9 flex-1 sm:flex-none",
                difficulty === "easy" ? "bg-bg-tertiary dark:bg-gray-800" : ""
              )}
            >
              {t("games.difficulty.easy")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDifficulty("medium")}
              className={cn(
                "min-h-9 flex-1 sm:flex-none",
                difficulty === "medium" ? "bg-bg-tertiary dark:bg-gray-800" : ""
              )}
            >
              {t("games.difficulty.medium")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDifficulty("hard")}
              className={cn(
                "min-h-9 flex-1 sm:flex-none",
                difficulty === "hard" ? "bg-bg-tertiary dark:bg-gray-800" : ""
              )}
            >
              {t("games.difficulty.hard")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={backToMenu}
              className="min-h-9 w-full sm:ml-auto sm:w-fit"
            >
              {t("games.backToMenu")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamingSystem;
